import { Suspense, lazy, useEffect, useState } from "react";
import "@/components/glass/app-canvas.css";

/**
 * §v3 Liquid Glass / §v3.app-canvas-liquid-metal (2026-05-14)
 *
 * Full-bleed Aurora Dusk backdrop mounted ONCE at app root.
 *
 * Visual stack (back → front):
 *
 *   0. CSS var(--canvas-gradient)        — FCP-safe paint, renders
 *      before the WebGL chunk is parsed. Guarantees no flash of
 *      white/black on slow first paint.
 *   1. Paper Shaders <LiquidMetal>       — fit=cover + scale=2.5
 *      stretches a huge iridescent metaball blob to fill the
 *      viewport. shape='metaballs' renders organic flowing slabs
 *      of metallic surface; the chromatic shifts (shiftRed/Blue)
 *      paint the rim with a faint rainbow that distinguishes
 *      "metal" from "tinted blob." Lazy-loaded so the shader
 *      compile never blocks first paint.
 *   2. Two ambient orbs                   — soft tinted radial-
 *      gradient blurs drifting slowly across the canvas (28s / 34s
 *      loops). Add depth, never compete with content.
 *
 * Pointer-events: none on every layer — clicks/scroll pass through
 * to whatever React content is rendered above.
 *
 * Position: fixed; inset: 0; z-index: -1. Sits behind everything,
 * doesn't push layout, doesn't re-render on route change.
 *
 * Used by:
 *   - <AppShell>  — dashboard backdrop (variant="dashboard", default)
 *   - <LoginPage> — sign-in + post-logout backdrop (variant="login")
 *
 * §v3.app-canvas-variants-v2 (2026-05-14) — operator asked for the
 * ORIGINAL static aurora back on the dashboard. Login + post-logout
 * keep the LiquidMetal shader (the sign-in moment gets the showy
 * visual). The dashboard reverts to the pre-shader CSS-only aurora:
 * the magenta+teal radial gradient + SVG noise overlay already
 * defined on .xpand-canvas-bg in xpand-design.css.
 *
 *   variant       backdrop                     speed
 *   ─────────     ──────────────────────────   ─────
 *   "login"       LiquidMetal shader + orbs    0.02 (gentle drift)
 *   "dashboard"   Static CSS canvas-gradient   — (no animation)
 *                 + SVG noise overlay
 */

// Lazy-loaded so the ~5KB shader chunk loads AFTER first paint.
// Layer 0 (CSS gradient) is what the user sees pre-shader.
const LiquidMetal = lazy(() =>
  import("@paper-design/shaders-react").then((m) => ({
    default: m.LiquidMetal,
  })),
);

// One-time motion check. Users who flip the OS preference mid-session
// get the previous behaviour until reload — acceptable vs a media
// listener that re-renders the whole app on every change.
const reducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export interface AppCanvasProps {
  /** "login" = crisp, faster (0.08); "dashboard" = blurred, slower (0.04). */
  variant?: "login" | "dashboard";
}

// §aurora-glass (2026-06-16) — the mesh's two shader colours are now
// theme-driven via the --xc-mesh-back / --xc-mesh-tint CSS tokens (defined
// per theme in xpand-design.css :root + themes.css), so the SAME metaball
// animation recolours: deep indigo/violet on Aurora Dusk, pale pastel on the
// light themes. The shader props are JS (Paper Shaders can't read CSS vars),
// so we resolve the tokens with getComputedStyle and re-read on theme change.
const FALLBACK_MESH = { back: "#0c0e18", tint: "#8b3fd6" } as const;

function readMeshColors(): { back: string; tint: string } {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { ...FALLBACK_MESH };
  }
  const cs = getComputedStyle(document.documentElement);
  const back = cs.getPropertyValue("--xc-mesh-back").trim() || FALLBACK_MESH.back;
  const tint = cs.getPropertyValue("--xc-mesh-tint").trim() || FALLBACK_MESH.tint;
  return { back, tint };
}

// Login (+ post-logout) backdrop — the full animated LiquidMetal stack.
// Split into its own component so the theme hooks live ONLY on the path
// that uses them (the dashboard variant returns an empty host, no hooks).
function LoginCanvas() {
  const [mesh, setMesh] = useState(readMeshColors);

  useEffect(() => {
    // Re-resolve once on mount (covers the case where the theme class lands
    // between module init and mount) and on every theme switch — setTheme()
    // fires "xpand:theme-change" (see lib/theme.ts), driven by the sidebar
    // ThemeToggle.
    const reread = () => setMesh(readMeshColors());
    reread();
    window.addEventListener("xpand:theme-change", reread);
    return () => window.removeEventListener("xpand:theme-change", reread);
  }, []);

  const speed = reducedMotion ? 0 : 0.02;
  return (
    <div className="xpand-canvas-bg xc-host xc-host-login" aria-hidden="true">
      <Suspense fallback={null}>
        <LiquidMetal
          className="xc-mesh"
          shape="metaballs"
          fit="cover"
          scale={2.5}
          colorBack={mesh.back}
          colorTint={mesh.tint}
          repetition={3.5}
          softness={0.55}
          distortion={0.7}
          contour={0.75}
          shiftRed={0.18}
          shiftBlue={-0.14}
          angle={28}
          speed={speed}
        />
      </Suspense>
      <span className="xc-orb xc-orb-a" />
      <span className="xc-orb xc-orb-b" />
    </div>
  );
}

export function AppCanvas({ variant = "dashboard" }: AppCanvasProps) {
  // Dashboard — empty .xpand-canvas-bg host. xpand-design.css paints
  // its background (canvas-gradient) + ::after noise overlay + 118%
  // saturate, restoring the original static aurora.
  if (variant === "dashboard") {
    // §v3.aurora-shared (2026-05-25) — empty host. xpand-design.css paints the
    // base + ::after noise; app-canvas.css gives the BUSINESS (roze) tenant its
    // vibrant aurora wallpaper (the 5-gradient drift), scoped to
    // html[data-tenant="roze"] .xc-host-dashboard. No extra DOM.
    return (
      <div
        className="xpand-canvas-bg xc-host xc-host-dashboard"
        aria-hidden="true"
      />
    );
  }
  // Login (+ post-logout) — full LiquidMetal stack. The .xc-host-login
  // class triggers the app-canvas.css overrides that suppress the
  // ::after noise and neutralise the legacy saturate boost, since the
  // shader produces its own surface texture.
  return <LoginCanvas />;
}
